import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryHelper } from '@/@diffrag/hooks/useQueryHelper'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { api } from '@/api'
import { formatCurrencyAmount } from '@/constants/currencies'
import type {
  IAdminReferralStatsPartnerStats,
  IAdminReferralStatsReservationDetailRow,
  IAdminReferralStatsRevenueDetailRow,
} from 'api'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from '@/lib/toast'

type RevenueByCurrency = {
  currency: string
  amount: string
}

type ReferralStatsRow = IAdminReferralStatsPartnerStats & {
  id: number
  revenueByCurrency?: RevenueByCurrency[]
}

type FilterMode = 'all' | 'month' | 'range'

type DetailTab = 'revenue' | 'reservation'

type DetailState = {
  tab: DetailTab
  partnerId: number
  partnerCode: string
  revenueRows: IAdminReferralStatsRevenueDetailRow[]
  reservationRows: IAdminReferralStatsReservationDetailRow[]
}

const FILTER_OPTIONS: { value: FilterMode; label: string }[] = [
  { value: 'all', label: '총매출액' },
  { value: 'month', label: '월 매출액' },
  { value: 'range', label: '기간 직접 설정' },
]

const STATUS_LABELS: Record<string, string> = {
  RESERVATION_CONFIRMED: '예약확정',
  COMPLETED: '완료',
}

const DETAIL_TABLE_TEXT_CELL = 'text-left'
const DETAIL_TABLE_AMOUNT_CELL = 'text-right tabular-nums'

function resolveListParams(
  filterMode: FilterMode,
  month: string,
  fromDate: string,
  toDate: string,
) {
  if (filterMode === 'all') {
    return {}
  }
  if (filterMode === 'month') {
    const start = dayjs(`${month}-01`)
    if (!start.isValid()) {
      return {}
    }
    return {
      fromDate: start.format('YYYY-MM-DD'),
      toDate: start.endOf('month').format('YYYY-MM-DD'),
    }
  }
  return {
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
  }
}

function formatDetailDate(value: string) {
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value
}

function ClickableStat({
  children,
  onClick,
}: {
  children: React.ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className="text-left text-brand-600 underline-offset-2 hover:underline"
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
    >
      {children}
    </button>
  )
}

function ReferralStatsToolbar({
  filterMode,
  month,
  fromDate,
  toDate,
  onFilterChange,
}: {
  filterMode: FilterMode
  month: string
  fromDate: string
  toDate: string
  onFilterChange: (record: Record<string, string>) => void
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-gray-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {FILTER_OPTIONS.map(({ value, label }) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={filterMode === value ? 'default' : 'outline'}
              onClick={() => onFilterChange({ filter: value })}
            >
              {label}
            </Button>
          ))}
        </div>

        {filterMode === 'month' && (
          <Input
            type="month"
            className="h-8 w-[150px]"
            value={month}
            onChange={(event) => onFilterChange({ month: event.target.value })}
          />
        )}

        {filterMode === 'range' && (
          <>
            <Input
              type="date"
              className="h-8 w-[150px]"
              value={fromDate}
              onChange={(event) =>
                onFilterChange({ fromDate: event.target.value })
              }
            />
            <span className="text-xs text-gray-400">~</span>
            <Input
              type="date"
              className="h-8 w-[150px]"
              value={toDate}
              onChange={(event) => onFilterChange({ toDate: event.target.value })}
            />
          </>
        )}
      </div>

      <p className="text-xs text-gray-500">
        행 또는 예약 확정 건수·최종 매출액을 클릭하면 환자별 상세 내역을 확인할
        수 있습니다.
      </p>
    </div>
  )
}

function PartnerDetailDialog({
  detail,
  onClose,
  onTabChange,
}: {
  detail: DetailState | null
  onClose: () => void
  onTabChange: (tab: DetailTab) => void
}) {
  if (!detail) {
    return null
  }

  const isRevenueTab = detail.tab === 'revenue'
  const rows = isRevenueTab ? detail.revenueRows : detail.reservationRows
  const emptyMessage = isRevenueTab
    ? '매출 내역이 없습니다.'
    : '예약 내역이 없습니다.'

  return (
    <Dialog
      open={!!detail}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent className="gap-0 px-0 py-0 sm:max-w-[960px]">
        <DialogHeader className="space-y-0 border-b px-6 pb-4 pt-6 pr-12">
          <DialogTitle>추천인 실적 상세 — {detail.partnerCode}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 px-6 py-4">
          {(
            [
              ['revenue', '매출 내역'],
              ['reservation', '예약 내역'],
            ] as const
          ).map(([value, label]) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={detail.tab === value ? 'default' : 'outline'}
              onClick={() => onTabChange(value)}
            >
              {label}
            </Button>
          ))}
        </div>

        <div className="max-h-[60vh] overflow-auto px-6 pb-6">
          <div className="overflow-hidden rounded-md border border-gray-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={DETAIL_TABLE_TEXT_CELL}>
                    {isRevenueTab ? '매출일' : '일자'}
                  </TableHead>
                  <TableHead className={DETAIL_TABLE_TEXT_CELL}>환자명</TableHead>
                  <TableHead className={DETAIL_TABLE_TEXT_CELL}>시술명</TableHead>
                  <TableHead className={DETAIL_TABLE_AMOUNT_CELL}>
                    결제금액
                  </TableHead>
                  {!isRevenueTab && (
                    <TableHead className={DETAIL_TABLE_TEXT_CELL}>상태</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isRevenueTab
                  ? detail.revenueRows.map((item) => (
                      <TableRow
                        key={`${item.consultationRequestId}-${item.date}`}
                      >
                        <TableCell className={DETAIL_TABLE_TEXT_CELL}>
                          {formatDetailDate(item.date)}
                        </TableCell>
                        <TableCell className={DETAIL_TABLE_TEXT_CELL}>
                          {item.patientName}
                        </TableCell>
                        <TableCell className={DETAIL_TABLE_TEXT_CELL}>
                          {item.procedureName ?? '-'}
                        </TableCell>
                        <TableCell className={DETAIL_TABLE_AMOUNT_CELL}>
                          {formatCurrencyAmount(item.currency, item.amount)}
                        </TableCell>
                      </TableRow>
                    ))
                  : detail.reservationRows.map((item) => (
                      <TableRow
                        key={`${item.consultationRequestId}-${item.date}`}
                      >
                        <TableCell className={DETAIL_TABLE_TEXT_CELL}>
                          {formatDetailDate(item.date)}
                        </TableCell>
                        <TableCell className={DETAIL_TABLE_TEXT_CELL}>
                          {item.patientName}
                        </TableCell>
                        <TableCell className={DETAIL_TABLE_TEXT_CELL}>
                          {item.procedureName ?? '-'}
                        </TableCell>
                        <TableCell className={DETAIL_TABLE_AMOUNT_CELL}>
                          {item.amount && item.currency
                            ? formatCurrencyAmount(item.currency, item.amount)
                            : '-'}
                        </TableCell>
                        <TableCell className={DETAIL_TABLE_TEXT_CELL}>
                          {STATUS_LABELS[item.status] ?? item.status}
                        </TableCell>
                      </TableRow>
                    ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={isRevenueTab ? 4 : 5}
                      className="py-10 text-center text-gray-500"
                    >
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default function ReferralStats() {
  const { replaceQuery } = useQueryHelper()
  const filterMode = (useQueryValue('filter', 'all') || 'all') as FilterMode
  const month = useQueryValue('month', dayjs().format('YYYY-MM'))
  const fromDate = useQueryValue('fromDate', '')
  const toDate = useQueryValue('toDate', '')

  const listParams = useMemo(
    () => resolveListParams(filterMode, month, fromDate, toDate),
    [filterMode, month, fromDate, toDate],
  )

  const { data, isFetching } = useApi({
    id: 'referralStats',
    fn: api().adminReferralStats.adminReferralStatsList,
    params: listParams,
  })

  const tableData = useMemo(
    () =>
      data?.items.map((item) => ({
        ...item,
        id: item.referralPartnerId,
      })) ?? [],
    [data?.items],
  )

  const [detail, setDetail] = useState<DetailState | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const openDetail = async (row: ReferralStatsRow, tab: DetailTab = 'revenue') => {
    setDetailLoading(true)
    try {
      const params = {
        referralPartnerId: row.referralPartnerId,
        ...listParams,
      }
      const [revenueResult, reservationResult] = await Promise.all([
        api().adminReferralStats.revenueDetailsList(params),
        api().adminReferralStats.reservationDetailsList(params),
      ])
      setDetail({
        tab,
        partnerId: row.referralPartnerId,
        partnerCode: row.partnerCode,
        revenueRows: revenueResult.items,
        reservationRows: reservationResult.items,
      })
    } catch (error) {
      console.error(error)
      toast.error('상세 내역을 불러오지 못했습니다.')
    } finally {
      setDetailLoading(false)
    }
  }

  return (
    <ContentView>
      <div className="overflow-hidden rounded-md border bg-white">
        <ReferralStatsToolbar
          filterMode={filterMode}
          month={month}
          fromDate={fromDate}
          toDate={toDate}
          onFilterChange={replaceQuery}
        />

        <SimpleTable
          isLoading={isFetching || detailLoading}
          options={{
            disableNew: true,
            disableSort: true,
            disableSearch: true,
          }}
          data={tableData}
          onRowClick={(row) => void openDetail(row as ReferralStatsRow)}
          columns={[
            { key: 'partnerCode', label: '코드', isPrimary: true },
            { key: 'visitCount', label: '유입(링크 클릭)' },
            { key: 'consultationCount', label: '총 신청 건수' },
            {
              key: 'reservationCount',
              label: '예약 확정 건수',
              render: (row) => (
                <ClickableStat
                  onClick={() =>
                    void openDetail(row as ReferralStatsRow, 'reservation')
                  }
                >
                  {row.reservationCount}
                </ClickableStat>
              ),
            },
            {
              key: 'totalRevenue',
              label: '최종 매출액',
              render: (row) => {
                const statsRow = row as ReferralStatsRow
                if (statsRow.revenueByCurrency?.length) {
                  return (
                    <ClickableStat
                      onClick={() => void openDetail(statsRow, 'revenue')}
                    >
                      <span className="flex flex-col gap-1">
                        {statsRow.revenueByCurrency.map((item) => (
                          <span key={item.currency}>
                            {formatCurrencyAmount(item.currency, item.amount)}
                          </span>
                        ))}
                      </span>
                    </ClickableStat>
                  )
                }
                return (
                  <ClickableStat
                    onClick={() => void openDetail(statsRow, 'revenue')}
                  >
                    {statsRow.totalRevenue || '-'}
                  </ClickableStat>
                )
              },
            },
          ]}
        />
      </div>

      <PartnerDetailDialog
        detail={detail}
        onClose={() => setDetail(null)}
        onTabChange={(tab) =>
          setDetail((prev) => (prev ? { ...prev, tab } : prev))
        }
      />
    </ContentView>
  )
}
